import { AreaButton, AreaLabel, QuantityLabel, UnitButton } from '@/components/searchArea.styles';
import { LOCATION_URL, apiGetCall } from '@/api/apiDispatcher';
import { prefetchProducts } from '@/store/products-preload';
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '@/components/_common/Button';
import Icon from '@/utils/Icon';
import { LNG_URLS } from '@/utils/constants';
import Select from 'react-select';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';
import { useLangStore } from '@/store/lang-store';

const MainSearch = ({
  lang,
  modeBanner,
  setIsMenuOpen,
  id = 'main',
  isFirst = false,
  $isHome = false,
}) => {
  const { langStore } = useLangStore();
  // Ensure stable ID generation for react-select between server and client
  const instanceId = useMemo(() => `search-${id}-select`, [id]);

  const [quantity, setQuantity] = useState({ id: '1500', name: '1500' });
  const [productSelect, setProductSelect] = useState([]);
  const [productList, setProductList] = useState([]);
  const [areaSelect, setAreaSelected] = useState(null);
  const [areaList, setAreaList] = useState([]);
  const [inputAreaValue, setInputAreaValue] = useState('');
  const navigate = useNavigate();
  const [geoLocation, setGeoLocation] = useState(null);
  const [unit, setUnit] = useState('quantity');
  const [loadingArea, setLoadingArea] = useState(false);
  const [, setButtonIsEnabled] = useState(false);
  const [areaError, setAreaError] = useState(false);
  const [productError, setProductError] = useState(false);
  const [unitError, setUnitError] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const location = useLocation();

  const urlArray = useMemo(() => {
    const arr = [];
    location.search
      ?.substring(1)
      ?.split('&')
      .forEach((el) => {
        const parts = el.split('=');
        if (parts.length === 2) {
          arr[parts[0]] = parts[1];
        }
      });
    return arr;
  }, [location.search]);

  function handleLocationClick(e) {
    e.preventDefault();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        setGeoLocation({ latitude, longitude });
        //console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);
      }, null);
    } else {
      console.log(lang?.form.NoGeolocation);
    }
  }

  useEffect(() => {
    if (geoLocation && isMounted) {
      setLoadingArea(true);

      apiGetCall(LOCATION_URL, langStore, {
        lat: geoLocation?.latitude,
        lng: geoLocation?.longitude,
        maxLevel: 6,
        minLevel: 5,
      })
        .then((data) => {
          startTransition(() => {
            setLoadingArea(false);
            data?.data && setAreaList(data?.data);
            data?.data && setAreaSelected(data?.data[0]);
          });
        })
        .catch((error) => {
          console.log(error);
          startTransition(() => {
            setLoadingArea(false);
          });
        });
    }
  }, [geoLocation, langStore, isMounted]);

  function initArea(lastArea) {
    startTransition(() => {
      setAreaList([]);
      if (lastArea) {
        setAreaSelected(lastArea);
        const code = lastArea.postalCode || lastArea.zip || '';
        const name = lastArea.name || lastArea.label || '';
        setInputAreaValue(code && name ? `${code} ${name}` : name || code || '');
        let lastAreas = [];
        lastAreas.push(lastArea);
        setAreaList(lastAreas);
      }
    });
  }

  const initSearch = useCallback(() => {
    if (typeof window === 'undefined') return;

    startTransition(() => {
      const lastUnit = urlArray?.by
        ? urlArray?.by === 'quantity'
          ? 'quantity'
          : 'budget'
        : localStorage.getItem('unit') !== 'undefined' && localStorage.getItem('unit') !== null
          ? JSON.parse(localStorage.getItem('unit'))
          : lang?.home?.mainsearch?.default?.by || 'liter';
      setUnit(lastUnit);

      const lastQty = urlArray?.for
        ? { id: urlArray?.for, name: urlArray?.for }
        : localStorage.getItem('quantity') !== 'undefined' &&
            localStorage.getItem('quantity') !== null
          ? JSON.parse(localStorage.getItem('quantity'))
          : {
              id: lang?.home?.mainsearch?.default?.quantity || '1000',
              name: lang?.home?.mainsearch?.default?.quantity || '1000',
            };
      setQuantity(lastQty);
    });

    if (urlArray?.areaCode)
      apiGetCall(LOCATION_URL + '/' + urlArray?.areaCode, langStore).then((data) => {
        if (data?.data && data?.data?.toString().toLowerCase() !== 'ok') {
          if (typeof window !== 'undefined') {
            // Ensure consistent data type for sessionStorage.getItem
            // The original line `sessionStorage.getItem(JSON.stringify(data?.data))` looks suspicious/incorrect.
            // It seems it was intended to serve as a cache check or similar, but the usage is odd.
            // For safety and hydration stability, I'm wrapping the state updates in startTransition as before.
          }
          startTransition(() => {
            setAreaList([]);
            setAreaSelected(data?.data);
            const code = data?.data?.postalCode || data?.data?.zip || '';
            const name = data?.data?.name || data?.data?.label || '';
            setInputAreaValue(code && name ? `${code} ${name}` : name || code || '');
            let lastAreas = [];
            lastAreas.push(data?.data);
            setAreaList(lastAreas);
          });
        }
      });
    else {
      if (
        typeof window !== 'undefined' &&
        sessionStorage?.getItem('area') &&
        sessionStorage?.getItem('area') !== 'undefined' &&
        sessionStorage?.getItem('area').toLowerCase() !== 'ok'
      ) {
        initArea(JSON.parse(sessionStorage.getItem('area')));
      }
    }

    // Wrap the product fetch in startTransition as well, but critically, move the fetch outside
    // or keep it but ensure the state update is consistently transitioned.
    prefetchProducts(langStore).then((data) => {
      startTransition(() => {
        setProductList(data?.data);
        const lastProduct = urlArray?.productId
          ? { id: Number(urlArray?.productId) }
          : localStorage.getItem('product') !== 'undefined' &&
              localStorage.getItem('product') !== null
            ? JSON.parse(localStorage.getItem('product'))
            : {
                id: lang?.home?.mainsearch?.default?.product || 1,
                name: lang?.home?.mainsearch?.default?.product || 'Mazout de chauffage',
              };
        if (lastProduct) {
          if (data?.data.length > 0) {
            data?.data.forEach((product) => {
              if (product.id === lastProduct.id) {
                setProductSelect(product);
              }
            });
          }
        }
      });
    });
  }, [lang, langStore, urlArray]);

  useEffect(() => {
    if (typeof window !== 'undefined' && isMounted) {
      // Only run initSearch after mounting and hydration to prevent mismatch
      // However, since we track isMounted, let's defer it slightly using specific check
      // or rely on startTransition which we added inside initSearch.
      startTransition(() => {
        initSearch();
      });
    }
  }, [initSearch, isMounted]);

  const onInputChange = (inputValue, { action }) => {
    if (action === 'input-change') {
      setInputAreaValue(inputValue);

      if (inputValue.length >= 3) {
        apiGetCall(LOCATION_URL, langStore, {
          keyword: inputValue,
          maxLevel: 6,
          minLevel: 5,
        }).then((data) => {
          startTransition(() => {
            let areaList = data?.data;
            if (areaSelect) areaList.unshift(areaSelect);
            setAreaList(areaList);
          });
        });
      } else {
        startTransition(() => {
          let areaList = [];
          if (areaSelect) areaList.push(areaSelect);
          setAreaList(areaList);
        });
      }
    }
  };

  const onAreaChange = (option) => {
    startTransition(() => {
      setAreaError(false);
      if (option) setAreaSelected(option);
      setInputAreaValue(option && option?.label ? option.label : '');
      let lastAreas = [];
      if (option) lastAreas.push(option);
      setAreaList(lastAreas);
    });
  };

  const onAreaClose = () => {
    startTransition(() => {
      setInputAreaValue(areaSelect ? areaSelect.label : '');
    });
  };

  const searchOffers = (event) => {
    event.preventDefault();
    startTransition(() => {
      if (!areaSelect) {
        setAreaError(true);
      }
      if (productSelect.length === 0) {
        setProductError(true);
      }
      if (unit === 'quantity' && !quantity) {
        setUnitError(true);
      }
      if (areaSelect && productSelect && ((unit === 'quantity' && quantity) || unit === 'budget')) {
        const urlToRedirect = `/${LNG_URLS[langStore]}/offers?areaCode=${areaSelect.id}&productId=${
          productSelect.id
        }&for=${unit === 'quantity' ? quantity.id : quantity}&by=${unit}`;
        localStorage.setItem('product', JSON.stringify(productSelect));
        localStorage.setItem('unit', JSON.stringify(unit));
        localStorage.setItem('quantity', JSON.stringify(quantity));
        sessionStorage.removeItem('cart');
        sessionStorage.setItem(
          'area',
          JSON.stringify({
            id: areaSelect.id,
            label: areaSelect.label,
            zip: areaSelect.zip,
          }),
        );
        if (setIsMenuOpen) setIsMenuOpen(false);

        navigate(urlToRedirect);
      }
    });
  };

  useEffect(() => {
    if (areaSelect?.areaCode && quantity?.id && productSelect?.id) {
      setButtonIsEnabled(true);
    } else {
      setButtonIsEnabled(false);
    }
  }, [areaSelect?.areaCode, quantity?.id, productSelect?.id]);

  useEffect(() => {
    if (areaSelect?.areaCode && quantity?.id && productSelect?.id) {
      setButtonIsEnabled(true);
    } else {
      setButtonIsEnabled(false);
    }
  }, [inputAreaValue, areaSelect?.areaCode, quantity?.id, productSelect?.id]);

  const UnitToRender = () => {
    return unit === 'budget' ? '€' : 'L';
  };

  const SelectUnit = () => {
    return (
      <>
        <UnitButton
          className={unit === 'quantity' && 'active'}
          onClick={() => {
            {
              setUnit('quantity');
              setQuantity({ id: '1500', name: '1500' });
            }
          }}>
          <span>L</span>
        </UnitButton>
        <UnitButton
          className={unit === 'budget' && 'active'}
          onClick={() => {
            {
              setUnit('budget');
              setQuantity({ id: '1000', name: '1000' });
            }
          }}>
          <span>€</span>
        </UnitButton>
      </>
    );
  };

  if (!isMounted) {
    const fieldHeight = $isHome ? '50px' : '38px';
    const buttonHeight = $isHome ? '50px' : '38px';
    const gap = $isHome ? '24px' : '8px';

    const containerStyle = {
      display: 'flex',
      flexDirection: $isHome ? 'column' : 'row',
      gap: gap,
      width: '100%',
      alignItems: $isHome ? 'stretch' : 'center',
      maxHeight: $isHome ? 'none' : '60px',
    };

    return (
      <div id={id} className={$isHome ? '' : 'headerSearch'} style={containerStyle}>
        {/* Area field with position icon */}
        <div
          style={{
            position: 'relative',
            flex: $isHome ? 'none' : '1',
            minWidth: $isHome ? 'auto' : '200px',
          }}>
          <div
            style={{
              height: fieldHeight,
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #ccc',
              paddingRight: '40px',
            }}></div>
          <div
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              background: '#f5f5f5',
              border: '1px solid #ddd',
            }}></div>
        </div>

        {/* Product select */}
        <div
          style={{
            height: fieldHeight,
            background: '#fff',
            borderRadius: '4px',
            border: '1px solid #ccc',
            flex: $isHome ? 'none' : '1',
            minWidth: $isHome ? 'auto' : '150px',
          }}></div>

        {/* Quantity + Unit buttons */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flex: $isHome ? 'none' : '0.8',
            minWidth: $isHome ? 'auto' : '120px',
          }}>
          <div
            style={{
              height: fieldHeight,
              flex: 1,
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}></div>
          <div
            style={{
              height: fieldHeight,
              width: '32px',
              background: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}></div>
          <div
            style={{
              height: fieldHeight,
              width: '32px',
              background: '#f5f5f5',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}></div>
        </div>

        {/* Submit button */}
        <div
          style={{
            height: buttonHeight,
            background: '#008579',
            borderRadius: '4px',
            flex: $isHome ? 'none' : '0',
            minWidth: $isHome ? 'auto' : '120px',
          }}></div>
      </div>
    );
  }

  return (
    <Form className='form-wrapper' onSubmit={searchOffers} noValidate $isHome={$isHome}>
      <AreaLabel htmlFor={`area-${id}`} className={areaError ? 'error' : ''}>
        {isMounted ? (
          <Select
            // aria-activedescendant={undefined} // Attempt to fix hydration mismatch
            placeholder={
              modeBanner
                ? lang?.home?.mainsearch?.locality || 'Localité'
                : lang?.home?.mainsearch?.cpLocality || 'Code postal ou localité'
            }
            inputId={`area-${id}`}
            name='area'
            disabled={false}
            options={areaList}
            value={areaSelect}
            onChange={onAreaChange}
            onMenuClose={onAreaClose}
            hideSelectedOptions={false}
            getOptionLabel={(option) => {
              if (!option) return '';
              const code = option?.postalCode || option?.zip || '';
              const name = option?.name || option?.label || '';
              return code && name ? `${code} ${name}` : name || code || '';
            }}
            getOptionValue={(option) => option?.id}
            inputValue={inputAreaValue}
            onInputChange={onInputChange}
            required
            noOptionsMessage={() => ''}
            className='react-select-locality'
            classNamePrefix='react-select'
            autoFocus={isFirst}
            instanceId={`${instanceId}-area`}></Select>
        ) : (
          <div
            className='react-select-locality'
            style={{
              height: $isHome ? '50px' : '38px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              background: 'white',
            }}></div>
        )}
        <AreaButton
          type='button'
          onClick={handleLocationClick}
          data-tooltip-id='area-tooltip'
          data-tooltip-content={lang?.form.locate}
          className={loadingArea ? 'spinning' : ''}>
          <Icon icon={loadingArea ? 'loading' : 'position-pin'} size={14} />
          <Tooltip
            disableStyleInjection={false}
            id='area-tooltip'
            place={!modeBanner ? 'top' : 'bottom-end'}
            className='areaTooltip'
          />
        </AreaButton>
      </AreaLabel>
      <label htmlFor={`product-${id}`} className={productError ? 'error' : ''}>
        {isMounted ? (
          <Select
            inputId={`product-${id}`}
            name='product'
            disabled={false}
            options={productList}
            value={productSelect}
            hideSelectedOptions={false}
            onChange={setProductSelect}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            required
            classNamePrefix='react-select'
            instanceId={`${instanceId}-product`}
            components={{
              IndicatorSeparator: () => null,
            }}></Select>
        ) : (
          <div
            className='react-select__control'
            style={{
              height: $isHome ? '50px' : '38px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              background: 'white',
            }}></div>
        )}
      </label>
      <QuantityLabel htmlFor={`${unit}-${id}`} className={unitError ? 'error' : ''}>
        {isMounted ? (
          <Select
            inputId={`${unit}-${id}`}
            name={unit}
            options={lang?.home?.mainsearch?.forOptions?.[unit] || []}
            value={quantity}
            onChange={setQuantity}
            getOptionLabel={(option) => `${option.name} ${UnitToRender()}`}
            getOptionValue={(option) => option.id}
            required
            classNamePrefix='react-select'
            instanceId={`${instanceId}-quantity`}
          />
        ) : (
          <div
            className='react-select__control'
            style={{
              height: $isHome ? '50px' : '38px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              background: 'white',
              flex: 1,
            }}></div>
        )}
        <SelectUnit />
      </QuantityLabel>
      <Button type='submit' color='green' $small={modeBanner} $fullwidth={!modeBanner}>
        {lang?.home?.mainsearch?.label || 'Rechercher'}
      </Button>
    </Form>
  );
};

export default MainSearch;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  ${(props) =>
    props.$isHome &&
    `
    .react-select__control {
      height: 38px;
    }


  `}

  .react-select__placeholder {
    overflow: hidden;
    white-space: nowrap;
  }

  .spinning {
    color: ${(props) => props.theme.colors.white};
    background-color: ${(props) => props.theme.colors.blue};
    svg {
      animation: spin-animation 0.5s infinite;
      display: inline-block;
    }
  }

  label {
    &.error {
      .react-select-locality {
        .react-select__control {
          border: 1px solid ${(props) => props.theme.colors.orange};
          background-color: ${(props) => props.theme.colors.orangeLight};
          border-top-left-radius: 6px;
          border-bottom-left-radius: 6px;
        }
      }
    }
  }

  @keyframes spin-animation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }
`;
